import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { ekkoText } from "@/lib/ekkoDefaults";
import { stripHtml } from "@/lib/text";

const initials = (html) =>
  stripHtml(html).split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

export default function CoachesSection({ coaches, loading, content = {} }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const coachesTitle = ekkoText(content, "coaches_title");
  const coachesSubtitle = ekkoText(content, "coaches_subtitle");

  return (
    <section
      id="equipa"
      ref={ref}
      className="py-24 md:py-32 bg-[#010000] relative"
      data-testid="coaches-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-16"
        >
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F5F5F5] mb-4 tracking-wider rich-text-content"
            data-testid="coaches-title"
            dangerouslySetInnerHTML={{ __html: coachesTitle }}
          />
          <p 
            className="text-[#C9C9C9] text-base md:text-lg max-w-2xl mx-auto rich-text-content"
            data-testid="coaches-subtitle"
            dangerouslySetInnerHTML={{ __html: coachesSubtitle }}
          />
        </motion.div>

        {/* Coaches Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-96 bg-[#1A1A1A]" />
            ))}
          </div>
        ) : (
          <div
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            data-testid="coaches-grid"
          >
            {coaches.map((coach, index) => (
              <motion.div
                key={stripHtml(coach.nome) || index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                className="coach-card relative bg-[#111111] border border-[#2A2A2A] overflow-hidden group cursor-pointer hover:border-[#A6C48A]"
                data-testid={`coach-card-${index}`}
              >
                {/* Image */}
                <div className="aspect-[3/4] overflow-hidden bg-[#1F1F1F]">
                  {coach.image ? (
                    <img
                      src={coach.image}
                      alt={stripHtml(coach.nome)}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  ) : (
                    // Sem foto ainda: iniciais sobre o verde da marca, para o cartão não ficar vazio
                    <div className="w-full h-full flex items-start justify-center pt-[22%] bg-[#A6C48A]" aria-hidden="true">
                      <span className="text-7xl font-bold text-[#F5F5F5]/70">{initials(coach.nome)}</span>
                    </div>
                  )}
                </div>

                {/* Content Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6 z-10 bg-[#111111]/95 border-t border-[#2A2A2A]">
                  <div
                    className="text-2xl font-bold text-[#F5F5F5] tracking-wider rich-text-content"
                    data-testid={`coach-name-${index}`}
                    dangerouslySetInnerHTML={{ __html: coach.nome }}
                  />
                  <div 
                    className="text-[#A6C48A] text-sm uppercase tracking-wider mb-2 rich-text-content"
                    dangerouslySetInnerHTML={{ __html: coach.role }}
                  />
                  {stripHtml(coach.frase) && (
                    <div className="text-[#C9C9C9] text-sm italic rich-text-content">
                      “{stripHtml(coach.frase)}”
                    </div>
                  )}
                </div>

                {/* Hover Border Effect */}
                <div className="absolute inset-0 border-2 border-[#A6C48A] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Section Divider */}
      <div className="section-divider mt-24" />
    </section>
  );
}
